/** @jsx jsx */
import { jsx, ThemeUIStyleObject } from "theme-ui";
import React from "react";
import { AspectRatio } from "@shared-components/AspectRatio/AspectRatio";
import { Box } from "@shared-components/Box/Box";
import { ButtonLink } from "@shared-components/Button/Button";
import {
  downloadAspectStyles,
  downloadButtonStyle,
  downloadStyle,
  downloadTextBoxStyle,
  downloadHeadingTextStyle,
  labelDownloadTextBoxStyle,
} from "./styles";
import { Heading } from "@shared-components/Heading/Heading";
import { UmbracoBackgroundImage } from "@shared-components/Image/UmbracoBackgroundImage/UmbracoBackgroundImage";
import { useDictionaryContext } from "../../context/dictionary/useDictionaryContext";
import { DICTIONARY_MAP } from "../../carlsberg-headless/dictionary-map";
import { useSiteContext } from "../../context/site/useSiteContext";
import { useWindowSize } from "../../shared/hooks/useWindowSize/useWindowSize";
import { VideoPlayer } from "@modules/MyBottleCollection/VideoPlayer/VideoPlayer";

export interface DownloadProps {
  sx?: ThemeUIStyleObject;
  theme: string;
  cbKey: string;
  title: string;
  description: string;
  slide: {
    format: string;
    src: string;
    noSound?: boolean;
  };
  variant: "brew" | "hybrid" | "mission" | "label" | "process";
  videoTitle?: string;
  HOCSessionId?: string;
}

const VideoImageDownload: React.FC<DownloadProps> = ({
  cbKey,
  title,
  sx,
  slide,
  variant,
  theme,
  videoTitle,
  HOCSessionId,
}) => {
  const { get } = useDictionaryContext();
  const { currentCulture } = useSiteContext();
  const textBoxStyle =
    variant === "label" ? labelDownloadTextBoxStyle : downloadTextBoxStyle;
  const isDark = theme === "dark";
  let ratio;

  if (variant === "label") {
    ratio = "16:9";
  } else if (variant === "hybrid" || variant === "brew") {
    ratio = "9:16";
  } else if (variant === "process") {
    ratio = "1:1";
  } else {
    ratio = "37:50";
  }

  const windowSize = useWindowSize();
  const isMobile = windowSize.width && windowSize.width < 768;

  const imageWidth = (
    value: "brew" | "hybrid" | "mission" | "label" | "process"
  ) => {
    switch (value) {
      case "hybrid":
        return "420";
      case "brew":
        return "450";
      case "mission":
        return "560";
      case "process":
        return "560";
      case "label":
        return isMobile ? "700" : "1110";
    }
  };

  return (
    <Box
      key={cbKey}
      sx={{
        ...downloadStyle,
        ...sx,
        color: isDark ? "white" : "darkGreen",
        maxWidth:
          variant === "hybrid"
            ? "420px"
            : variant === "mission"
            ? "560px"
            : variant === "label"
            ? "700px"
            : "560px",
      }}
    >
      <AspectRatio ratio={ratio} sx={{ ...downloadAspectStyles }}>
        {(slide.format.split("/")[0] === "video" || slide.format === "mp4") &&
          slide.src && (
            <VideoPlayer
              src={
                slide.src.includes("cloudinary")
                  ? `${slide.src.split("upload/")[0]}upload/q_80/w_${imageWidth(
                      variant
                    )}/${slide.src.split("upload/")[1]}`
                  : slide.src
              }
              extention={"mp4"}
              noSound={slide.noSound}
              videoTitle={videoTitle}
            />
          )}
        {slide.format.split("/")[0] != "video" &&
          (slide.format.split("/")[0] === "image" || slide.format === "png") &&
          slide.src && (
            <UmbracoBackgroundImage
              image={{
                alias: null,
                url: null,
                fullUrl: slide.src.includes("cloudinary")
                  ? `${slide.src.split("upload/")[0]}upload/w_${imageWidth(
                      variant
                    )}/${slide.src.split("upload/")[1]}`
                  : slide.src,
                width: 4,
                height: 8,
                extension: null,
                size: null,
                focalPoint: { top: 0, left: 0 },
                altText: null,
              }}
              className={HOCSessionId}
              sx={{ objectFit: "contain" }}
            />
          )}
      </AspectRatio>
      <Box sx={textBoxStyle} className="download">
        {slide?.src && (
          <ButtonLink
            to={
              slide.src.includes("cloudinary")
                ? `${slide.src.split("upload/")[0]}upload/fl_attachment/${
                    slide.src.split("upload/")[1]
                  }`
                : slide.src
            }
            download
            variant={isDark ? "white" : "green"}
            outline
            sx={downloadButtonStyle}
            className={HOCSessionId}
          >
            {get(
              DICTIONARY_MAP.digital_memories.download_module_button_text,
              currentCulture
            ) ?? ""}
          </ButtonLink>
        )}
        {title && (
          <Box sx={{ pt: [4] }}>
            <Heading as="h3" sx={downloadHeadingTextStyle}>
              {title}
            </Heading>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export { VideoImageDownload };
